import { Alert, alpha, Box, Chip, Typography } from '@mui/material';
import React from 'react';

import {
  InteractionFormDensityContext,
  useInteractionFormDensity,
} from './InteractionFormDensityContext';
import {
  InteractionFieldBlockProps,
  InteractionFormShellProps,
  InteractionNoticeProps,
  InteractionNoticeSeverity,
} from './InteractionFormShell.props';
import {
  getInteractionFormTokens,
  InteractionFormDensity,
  interactionCardSx,
  interactionFieldStackSx,
} from './interactionFormTokens';

/**
 * The shared card every interaction form sits in, on every surface — the mobile
 * status-update sheet, mobile part actions, Create Service and the desktop
 * status-change dialog. Consumers keep their own field renderers and logic;
 * this owns only the chrome and the vertical rhythm, so the forms read as one
 * system instead of three.
 */
export const InteractionFormShell: React.FC<InteractionFormShellProps> = ({
  title,
  isMandatory,
  countLabel,
  density = InteractionFormDensity.Compact,
  children,
}) => {
  const tokens = getInteractionFormTokens(density);

  return (
    <InteractionFormDensityContext.Provider value={density}>
      <Box sx={interactionCardSx(tokens)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: tokens.headerGap,
          }}
        >
          <Typography sx={{ fontSize: tokens.formTitleFontSize, fontWeight: 600 }}>
            {title}
          </Typography>
          {isMandatory && (
            <Chip
              label="Mandatory"
              size="small"
              // A tint with dark text, not white-on-warning.main: at 9px this is
              // normal-size text, so it needs 4.5:1, and white fails on both
              // warning.main (~3.0:1) and warning.dark (~3.8:1). The label word
              // carries the meaning, so the colour is reinforcement, not the
              // signal — WCAG 1.4.1.
              sx={{
                height: 18,
                fontSize: '9px',
                fontWeight: 600,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
                color: 'text.primary',
              }}
            />
          )}
          {countLabel && (
            <Typography sx={{ fontSize: tokens.helperFontSize, color: 'text.secondary' }}>
              {countLabel}
            </Typography>
          )}
        </Box>
        <Box sx={interactionFieldStackSx(tokens)}>{children}</Box>
      </Box>
    </InteractionFormDensityContext.Provider>
  );
};

/**
 * One field: an external label above the control, optional guidance, and the
 * validation message below. Pair with `hideInlineLabel` on the control atoms so
 * the same label never renders twice (coding-guidelines.md §2.9).
 */
export const InteractionFieldBlock: React.FC<InteractionFieldBlockProps> = ({
  label,
  required,
  helper,
  error,
  hideLabel,
  children,
}) => {
  const tokens = getInteractionFormTokens(useInteractionFormDensity());
  const showLabel = !hideLabel && !!label;

  return (
    <Box>
      {showLabel && (
        <Typography
          sx={{ fontSize: tokens.fieldLabelFontSize, fontWeight: 600, mb: 0.5 }}
          component="p"
        >
          {label}
          {required ? ' *' : ''}
        </Typography>
      )}
      {helper && (
        <Typography sx={{ fontSize: tokens.helperFontSize, color: 'text.secondary', mb: 0.5 }}>
          {helper}
        </Typography>
      )}
      <Box
        sx={
          tokens.minControlHeight
            ? {
                '& .MuiInputBase-root': { minHeight: tokens.minControlHeight },
                '& .MuiButtonBase-root': { minHeight: tokens.minControlHeight },
              }
            : undefined
        }
      >
        {children}
      </Box>
      {error && (
        <Typography
          role="alert"
          sx={{ fontSize: '11px', color: 'error.main', mt: 0.5, fontWeight: 500 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

/**
 * The one notice surface for interaction forms — submit errors, block messages
 * and bypass warnings. Replaces the separately hand-rolled error boxes each
 * surface used to carry.
 */
export const InteractionNotice: React.FC<InteractionNoticeProps> = ({
  severity = InteractionNoticeSeverity.Error,
  children,
}) => {
  const tokens = getInteractionFormTokens(useInteractionFormDensity());

  return (
    <Alert
      severity={severity}
      sx={{
        fontSize: tokens.helperFontSize,
        py: 0.5,
        alignItems: 'center',
        '& .MuiAlert-message': { py: 0.5 },
      }}
    >
      {children}
    </Alert>
  );
};
